import { useState, useEffect } from "react";
import api from "../../services/api";

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=Fraunces:ital,opsz,wght@0,9..144,600&display=swap');
  .aud-root { font-family: 'DM Sans', system-ui, sans-serif; }
  @keyframes audFadeUp { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
  @keyframes audSpin   { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
  .aud-period-btn { transition:all 0.2s; cursor:pointer; }
  .aud-period-btn:hover { background:rgba(102,126,234,0.1) !important; }
  .aud-period-btn.active { background:linear-gradient(135deg,#1A5EA8,#0B2545) !important; color:white !important; border-color:transparent !important; }
  .aud-row { transition:all 0.15s; cursor:pointer; }
  .aud-row:hover { background:#f8faff !important; }
  .aud-row.expanded { background:#f0f4ff !important; }
  .aud-detail-row:hover { background:#f5f7ff !important; }
`;

const PERIODS = [
  { label: "Today",    value: "1d",  days: 1  },
  { label: "7 Days",   value: "7d",  days: 7  },
  { label: "2 Weeks",  value: "14d", days: 14 },
  { label: "Month",    value: "30d", days: 30 },
];

const ENDPOINT_COLORS = {
  "/v1/providers/nearby":  { bg: "#eef2ff", color: "#1A5EA8" },
  "/v1/providers/search":  { bg: "#f0fdf4", color: "#059669" },
  "/v1/providers/amyloid": { bg: "#fdf4ff", color: "#9333ea" },
  "/v1/providers/zip":     { bg: "#fff7ed", color: "#ea580c" },
};

function Spinner({ size = 20 }) {
  return <div style={{ width: size, height: size, borderRadius: "50%",
    border: "2px solid #e5e7eb", borderTopColor: "#1A5EA8",
    animation: "audSpin 0.7s linear infinite", flexShrink: 0 }} />;
}

function StatCard({ label, value, sub, color = "#1A5EA8", icon }) {
  return (
    <div style={{ background: "white", borderRadius: 16, padding: "20px 24px",
      boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
      borderTop: `3px solid ${color}`, animation: "audFadeUp 0.4s ease both" }}>
      <div style={{ fontSize: 28, marginBottom: 4 }}>{icon}</div>
      <div style={{ fontSize: 28, fontWeight: 700, color, lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 12, fontWeight: 600, color: "#374151", marginTop: 4 }}>{label}</div>
      {sub && <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function EndpointBadge({ endpoint }) {
  const short = endpoint?.replace("/v1/providers/", "") || endpoint;
  const c = ENDPOINT_COLORS[endpoint] || { bg: "#f3f4f6", color: "#6b7280" };
  return (
    <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 999,
      background: c.bg, color: c.color, textTransform: "uppercase", letterSpacing: "0.06em" }}>
      {short}
    </span>
  );
}

export default function ApiUsageDashboard() {
  const [period,    setPeriod]    = useState("7d");
  const [stats,     setStats]     = useState(null);
  const [logs,      setLogs]      = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [expanded,  setExpanded]  = useState(null);
  const [animateIn, setAnimateIn] = useState(false);
  const [page,      setPage]      = useState(0);
  const PAGE_SIZE = 20;

  useEffect(() => {
    loadData();
    setTimeout(() => setAnimateIn(true), 100);
  }, [period]);

  const loadData = async () => {
    setLoading(true);
    try {
      const days = PERIODS.find(p => p.value === period)?.days || 7;
      const [statsRes, logsRes] = await Promise.all([
        api.get("/v1/providers/stats"),
        api.get("/v1/providers/logs", { params: { days, limit: 500 } }),
      ]);
      setStats(statsRes.data);
      setLogs(logsRes.data || []);
      setPage(0);
    } catch (e) {
      console.error("Failed to load usage data:", e);
    } finally {
      setLoading(false);
    }
  };

  const filteredLogs = logs.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const totalPages   = Math.ceil(logs.length / PAGE_SIZE);

  const endpointCounts = logs.reduce((acc, log) => {
    const ep = log.endpoint || "unknown";
    acc[ep] = (acc[ep] || 0) + 1;
    return acc;
  }, {});

  const uniqueIps = new Set(logs.map(l => l.ipAddress)).size;
  const avgDuration = logs.length
    ? Math.round(logs.reduce((s, l) => s + (l.durationMs || 0), 0) / logs.length)
    : 0;

  return (
    <div className="aud-root" style={{ background: "#f8faff", minHeight: "100vh",
      padding: "32px 24px 80px" }}>
      <style>{STYLES}</style>

      {/* Header */}
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <div style={{ marginBottom: 28, animation: animateIn ? "audFadeUp 0.4s ease both" : "none" }}>
          <h1 style={{ fontFamily: "'Fraunces',serif", fontSize: 28, fontWeight: 700,
            color: "#0D2B45", margin: "0 0 6px" }}>
            📊 API Usage Dashboard
          </h1>
          <p style={{ fontSize: 14, color: "#6b7280", margin: 0 }}>
            Provider Finder API — public endpoint monitoring
          </p>
        </div>

        {/* Period selector */}
        <div style={{ display: "flex", gap: 6, marginBottom: 24,
          animation: animateIn ? "audFadeUp 0.4s ease 0.05s both" : "none" }}>
          {PERIODS.map(p => (
            <button key={p.value}
              className={`aud-period-btn ${period === p.value ? "active" : ""}`}
              onClick={() => setPeriod(p.value)}
              style={{ padding: "8px 18px", border: "1px solid #e5e7eb", borderRadius: 10,
                fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
                background: period === p.value ? "" : "white",
                color: period === p.value ? "" : "#6b7280" }}>
              {p.label}
            </button>
          ))}
          <button onClick={loadData}
            style={{ marginLeft: "auto", padding: "8px 16px", background: "white",
              border: "1px solid #e5e7eb", borderRadius: 10, fontSize: 13,
              fontWeight: 600, cursor: "pointer", fontFamily: "inherit", color: "#1A5EA8",
              display: "flex", alignItems: "center", gap: 6 }}>
            {loading ? <Spinner size={13} /> : "↺"} Refresh
          </button>
        </div>

        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 80 }}>
            <Spinner size={36} />
          </div>
        ) : (
          <>
            {/* Stat cards */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))",
              gap: 16, marginBottom: 28,
              animation: animateIn ? "audFadeUp 0.4s ease 0.1s both" : "none" }}>
              <StatCard icon="🔢" label="Total Requests"
                value={logs.length.toLocaleString()}
                sub={`in last ${PERIODS.find(p=>p.value===period)?.label}`}
                color="#1A5EA8" />
              <StatCard icon="👥" label="Unique IPs"
                value={uniqueIps.toLocaleString()}
                sub="distinct callers"
                color="#0ab5a0" />
              <StatCard icon="⚡" label="Avg Response"
                value={`${avgDuration}ms`}
                sub="average duration"
                color="#f59e0b" />
              <StatCard icon="🔍" label="Most Used"
                value={Object.entries(endpointCounts).sort((a,b)=>b[1]-a[1])[0]?.[0]?.replace("/v1/providers/","") || "—"}
                sub="top endpoint"
                color="#0B2545" />
            </div>

            {/* Endpoint breakdown */}
            <div style={{ background: "white", borderRadius: 16, padding: "20px 24px",
              boxShadow: "0 2px 12px rgba(0,0,0,0.06)", marginBottom: 24,
              animation: animateIn ? "audFadeUp 0.4s ease 0.15s both" : "none" }}>
              <h2 style={{ fontFamily: "'Fraunces',serif", fontSize: 16, fontWeight: 600,
                color: "#0D2B45", margin: "0 0 16px" }}>Requests by Endpoint</h2>
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                {Object.entries(endpointCounts)
                  .sort((a, b) => b[1] - a[1])
                  .map(([ep, count]) => {
                    const pct = Math.round((count / logs.length) * 100);
                    const c = ENDPOINT_COLORS[ep] || { bg: "#f3f4f6", color: "#6b7280" };
                    return (
                      <div key={ep} style={{ flex: "1 1 180px", background: c.bg,
                        borderRadius: 12, padding: "14px 16px",
                        border: `1px solid ${c.color}20` }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: c.color,
                          textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>
                          {ep.replace("/v1/providers/", "")}
                        </div>
                        <div style={{ fontSize: 24, fontWeight: 700, color: "#0D2B45",
                          lineHeight: 1 }}>{count.toLocaleString()}</div>
                        <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>
                          {pct}% of total
                        </div>
                        {/* Mini bar */}
                        <div style={{ height: 4, background: "rgba(0,0,0,0.08)",
                          borderRadius: 999, marginTop: 8, overflow: "hidden" }}>
                          <div style={{ height: "100%", width: `${pct}%`,
                            background: c.color, borderRadius: 999, transition: "width 0.8s ease" }} />
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>

            {/* Detailed log table */}
            <div style={{ background: "white", borderRadius: 16,
              boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
              animation: animateIn ? "audFadeUp 0.4s ease 0.2s both" : "none" }}>

              <div style={{ padding: "20px 24px 16px", borderBottom: "1px solid #f3f4f6",
                display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <h2 style={{ fontFamily: "'Fraunces',serif", fontSize: 16, fontWeight: 600,
                  color: "#0D2B45", margin: 0 }}>
                  Request Log
                </h2>
                <span style={{ fontSize: 12, color: "#9ca3af" }}>
                  {logs.length} total · click row for details
                </span>
              </div>

              {/* Table header */}
              <div style={{ display: "grid",
                gridTemplateColumns: "1fr 140px 100px 80px 80px 100px",
                padding: "10px 20px", background: "#0D2B45",
                fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,0.6)",
                textTransform: "uppercase", letterSpacing: "0.08em" }}>
                <div>Endpoint</div>
                <div>IP Address</div>
                <div>Results</div>
                <div>Duration</div>
                <div>Time</div>
                <div>Referer</div>
              </div>

              {filteredLogs.length === 0 && (
                <div style={{ padding: "40px 20px", textAlign: "center", color: "#9ca3af" }}>
                  <div style={{ fontSize: 32, marginBottom: 8 }}>📭</div>
                  <p style={{ fontSize: 14 }}>No API calls recorded yet</p>
                </div>
              )}

              {filteredLogs.map((log, i) => (
                <div key={log.id || i}>
                  {/* Main row */}
                  <div className={`aud-row ${expanded === i ? "expanded" : ""}`}
                    onClick={() => setExpanded(expanded === i ? null : i)}
                    style={{ display: "grid",
                      gridTemplateColumns: "1fr 140px 100px 80px 80px 100px",
                      padding: "12px 20px", borderBottom: "1px solid #f3f4f6",
                      background: i % 2 === 0 ? "#fafafa" : "white",
                      alignItems: "center" }}>

                    <div><EndpointBadge endpoint={log.endpoint} /></div>

                    <div style={{ fontSize: 12, color: "#374151", fontFamily: "monospace" }}>
                      {log.ipAddress || "—"}
                    </div>

                    <div style={{ fontSize: 12, fontWeight: 600,
                      color: log.resultsCount > 0 ? "#059669" : "#9ca3af" }}>
                      {log.resultsCount ?? "—"} results
                    </div>

                    <div style={{ fontSize: 12, color: "#374151" }}>
                      {log.durationMs != null ? (
                        <span style={{ color: log.durationMs > 1000 ? "#dc2626" : "#374151" }}>
                          {log.durationMs}ms
                        </span>
                      ) : "—"}
                    </div>

                    <div style={{ fontSize: 11, color: "#9ca3af" }}>
                      {log.createdAt
                        ? new Date(log.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
                        : "—"}
                    </div>

                    <div style={{ fontSize: 10, color: "#9ca3af",
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {log.referer
                        ? new URL(log.referer).hostname
                        : <span style={{ color: "#d1d5db" }}>direct</span>}
                    </div>
                  </div>

                  {/* Expanded detail */}
                  {expanded === i && (
                    <div style={{ padding: "16px 24px", background: "#f0f4ff",
                      borderBottom: "1px solid #e0e7ff",
                      borderLeft: "4px solid #1A5EA8" }}>
                      <div style={{ display: "grid",
                        gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>

                        <div>
                          <p style={{ fontSize: 10, fontWeight: 700, color: "#1A5EA8",
                            textTransform: "uppercase", letterSpacing: "0.08em", margin: "0 0 4px" }}>
                            Full Endpoint
                          </p>
                          <p style={{ fontSize: 12, color: "#374151", margin: 0,
                            fontFamily: "monospace" }}>{log.endpoint}</p>
                        </div>

                        <div>
                          <p style={{ fontSize: 10, fontWeight: 700, color: "#1A5EA8",
                            textTransform: "uppercase", letterSpacing: "0.08em", margin: "0 0 4px" }}>
                            Parameters
                          </p>
                          <p style={{ fontSize: 11, color: "#374151", margin: 0,
                            fontFamily: "monospace", wordBreak: "break-all" }}>
                            {log.params || "—"}
                          </p>
                        </div>

                        <div>
                          <p style={{ fontSize: 10, fontWeight: 700, color: "#1A5EA8",
                            textTransform: "uppercase", letterSpacing: "0.08em", margin: "0 0 4px" }}>
                            Timestamp
                          </p>
                          <p style={{ fontSize: 12, color: "#374151", margin: 0 }}>
                            {log.createdAt
                              ? new Date(log.createdAt).toLocaleString()
                              : "—"}
                          </p>
                        </div>

                        <div style={{ gridColumn: "1 / -1" }}>
                          <p style={{ fontSize: 10, fontWeight: 700, color: "#1A5EA8",
                            textTransform: "uppercase", letterSpacing: "0.08em", margin: "0 0 4px" }}>
                            User Agent
                          </p>
                          <p style={{ fontSize: 11, color: "#6b7280", margin: 0,
                            wordBreak: "break-all" }}>
                            {log.userAgent || "—"}
                          </p>
                        </div>

                        {log.referer && (
                          <div style={{ gridColumn: "1 / -1" }}>
                            <p style={{ fontSize: 10, fontWeight: 700, color: "#1A5EA8",
                              textTransform: "uppercase", letterSpacing: "0.08em", margin: "0 0 4px" }}>
                              Referer
                            </p>
                            <a href={log.referer} target="_blank" rel="noopener noreferrer"
                              style={{ fontSize: 11, color: "#1A5EA8", wordBreak: "break-all" }}>
                              {log.referer}
                            </a>
                          </div>
                        )}

                        {log.apiKey && (
                          <div>
                            <p style={{ fontSize: 10, fontWeight: 700, color: "#1A5EA8",
                              textTransform: "uppercase", letterSpacing: "0.08em", margin: "0 0 4px" }}>
                              API Key
                            </p>
                            <p style={{ fontSize: 12, color: "#374151", margin: 0,
                              fontFamily: "monospace" }}>{log.apiKey}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {/* Pagination */}
              {totalPages > 1 && (
                <div style={{ padding: "16px 20px", display: "flex",
                  justifyContent: "space-between", alignItems: "center",
                  borderTop: "1px solid #f3f4f6" }}>
                  <button onClick={() => setPage(p => Math.max(0, p - 1))}
                    disabled={page === 0}
                    style={{ padding: "6px 14px", border: "1px solid #e5e7eb",
                      borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer",
                      fontFamily: "inherit", background: "white", color: "#374151",
                      opacity: page === 0 ? 0.4 : 1 }}>
                    ← Previous
                  </button>
                  <span style={{ fontSize: 12, color: "#6b7280" }}>
                    Page {page + 1} of {totalPages} · {logs.length} total requests
                  </span>
                  <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                    disabled={page >= totalPages - 1}
                    style={{ padding: "6px 14px", border: "1px solid #e5e7eb",
                      borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer",
                      fontFamily: "inherit", background: "white", color: "#374151",
                      opacity: page >= totalPages - 1 ? 0.4 : 1 }}>
                    Next →
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}